import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import { FaArrowRight } from "react-icons/fa";
import { NavLink, useLocation } from "react-router-dom";
import "./productgrid.css";
import Footer from "../../components/footer/footer";
import Header from "../../components/header/header";

const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

const Productgrid = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedSubcategory, setSelectedSubcategory] = useState("All");
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const location = useLocation();
  const [selectedBrand, setSelectedBrand] = useState("All");
  const disableAnimation = location.pathname === "/shop";


  useEffect(() => {
    const params = new URLSearchParams(location.search);

    setSelectedBrand(params.get("brand") || "All");
    setSelectedCategory(params.get("category") || "All");
    setSelectedSubcategory(params.get("subcategory") || "All");
  }, [location]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${REACT_APP_API_URL}/products`);
        const data = await res.json();
        setProducts(data);
      } catch (err) {
        console.error("Failed to fetch products", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);
  useEffect(() => {
    if (!products.length) return;

    let filtered = [...products];

    if (selectedBrand !== "All") {
      filtered = filtered.filter(
        p => p.brand?.toLowerCase() === selectedBrand.toLowerCase()
      );
    }

    const uniqueCategories = [
      ...new Set(
        filtered
          .map(p => p.category)
          .filter(Boolean)
      )
    ];

    setCategories(["All", ...uniqueCategories]);

    // Reset category if invalid
    if (
      selectedCategory !== "All" &&
      !uniqueCategories.includes(selectedCategory)
    ) {
      setSelectedCategory("All");
    }

  }, [products, selectedBrand]);
  useEffect(() => {
    if (!products.length) return;

    let filtered = [...products];

    if (selectedBrand !== "All") {
      filtered = filtered.filter(
        p => p.brand?.toLowerCase() === selectedBrand.toLowerCase()
      );
    }

    if (selectedCategory !== "All") {
      filtered = filtered.filter(
        p => p.category === selectedCategory
      );
    }

    const uniqueSubcategories = [
      ...new Set(
        filtered
          .map(p => p.subcategory)
          .filter(Boolean)
      )
    ];

    setSubcategories(["All", ...uniqueSubcategories]);

    // Reset subcategory if invalid
    if (
      selectedSubcategory !== "All" &&
      !uniqueSubcategories.includes(selectedSubcategory)
    ) {
      setSelectedSubcategory("All");
    }

  }, [products, selectedBrand, selectedCategory]);
  useEffect(() => {
    let filtered = [...products];

    if (selectedBrand !== "All") {
      filtered = filtered.filter(
        p => p.brand?.toLowerCase() === selectedBrand.toLowerCase()
      );
    }

    if (selectedCategory !== "All") {
      filtered = filtered.filter(
        p => p.category === selectedCategory
      );
    }

    if (selectedSubcategory !== "All") {
      filtered = filtered.filter(
        p => p.subcategory === selectedSubcategory
      );
    }

    setFilteredProducts(filtered);

  }, [products, selectedBrand, selectedCategory, selectedSubcategory]);
  // FIXED: Helper function to get the first product image
  const getFirstImage = (product) => {
    // Case 1: images is an array
    if (Array.isArray(product.images) && product.images.length > 0) {
      return product.images[0];
    }

    // Case 2: images is an object with color keys (like watches)
    if (product.images && typeof product.images === 'object' && !Array.isArray(product.images)) {
      // Get the first color key
      const colorKeys = Object.keys(product.images);
      if (colorKeys.length > 0) {
        const firstColor = colorKeys[0];
        const colorImages = product.images[firstColor];

        // If it's an array, get first image
        if (Array.isArray(colorImages) && colorImages.length > 0) {
          return colorImages[0];
        }
        // If it's a string, return it
        if (typeof colorImages === 'string') {
          return colorImages;
        }
      }
    }

    // Case 3: Check colors array for images
    if (product.colors && product.colors.length > 0) {
      const firstColor = product.colors[0];

      // Check if color has images
      if (firstColor.images) {
        // If images is an array
        if (Array.isArray(firstColor.images) && firstColor.images.length > 0) {
          return firstColor.images[0];
        }
        // If images is a string
        if (typeof firstColor.images === 'string') {
          return firstColor.images;
        }
        // If images is an object with front/back/side
        if (typeof firstColor.images === 'object') {
          return firstColor.images.front || firstColor.images.back || firstColor.images.side;
        }
      }

      // Check if color has its own image property
      if (firstColor.image) {
        return firstColor.image;
      }
    }

    // Case 4: Check if product has variants with images
    if (product.variants && product.variants.length > 0) {
      const firstVariant = product.variants[0];
      if (firstVariant.image) {
        return firstVariant.image;
      }
    }

    // Fallback
    return "/placeholder.jpg";
  };

  // Helper function to get display price
  const getDisplayPrice = (product) => {
    if (product.variants && product.variants.length > 0) {
      const prices = product.variants.map(v => v.price).filter(Boolean);
      if (prices.length > 0) {
        return Math.min(...prices);
      }
    }
    return product.price || 0;
  };

  // Helper function to get color name
  const getColorName = (product) => {
    if (product.variants && product.variants.length > 0) {
      const uniqueColors = [...new Set(product.variants.map(v => v.color))];
      return uniqueColors[0] || "";
    }
    if (product.colors && product.colors.length > 0) {
      return product.colors[0].name;
    }
    return "";
  };

  // Handle category click
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setSelectedSubcategory("All");

    // Update URL
    const url = category === "All"
      ? "/category"
      : `/category/${category}`;
    window.history.pushState({}, '', url);
  };

  // Handle subcategory click
  const handleSubcategoryClick = (subcategory) => {
    setSelectedSubcategory(subcategory);

    // Update URL with subcategory parameter
    if (selectedCategory !== "All") {
      if (subcategory === "All") {
        const url = `/category/${selectedCategory}`;
        window.history.pushState({}, '', url);
      } else {
        const url = `/category/${selectedCategory}?subcategory=${encodeURIComponent(subcategory)}`;
        window.history.pushState({}, '', url);
      }
    }
  };

  if (loading) {
    return (
      <div>
        <Header />
        <Container className="py-5 text-center">
          <div className="loading-spinner">Loading products...</div>
        </Container>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Header />

      <Container className="py-5">
        {/* Category Filter Bar */}
        <div className="category-filter-bar mb-3">
          <div className="category-filter-scroll">
            {categories.map((category) => (
              <button
                key={category}
                className={`category-filter-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => handleCategoryClick(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Subcategory Filter - Only show if there are subcategories with products */}
        {subcategories.length > 1 && (
          <div className="subcategory-filter mb-4">
            <div className="subcategory-scroll">
              {subcategories.map((subcategory) => (
                <button
                  key={subcategory}
                  className={`subcategory-chip ${selectedSubcategory === subcategory ? 'active' : ''}`}
                  onClick={() => handleSubcategoryClick(subcategory)}
                >
                  {subcategory}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Category Title */}
        <h2 className="category-title mb-4">
          {selectedBrand !== "All" && (
            <span className="me-2 text-primary text-capitalize">
              {selectedBrand}
            </span>
          )}

          {selectedCategory === "All"
            ? "Products"
            : selectedSubcategory === "All"
              ? selectedCategory
              : `${selectedCategory} - ${selectedSubcategory}`}

          <span className="product-count">
            ({filteredProducts.length} products)
          </span>
        </h2>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? null : (
          <Row className="g-4">
            {filteredProducts.map((item) => {
              const firstImage = getFirstImage(item);
              const displayPrice = getDisplayPrice(item);
              const colorName = getColorName(item);
              const colors = item.colors || [];

              return (
                <Col lg={3} md={6} sm={6} xs={6} key={item._id}>
                  <motion.div
                    className="product-card"
                    whileHover={disableAnimation ? {} : { y: -10 }}
                    transition={disableAnimation ? { duration: 0 } : { duration: 0.3 }}
                  >
                    <motion.img
                      src={firstImage}
                      alt={item.name}
                      className="product-img"
                      whileHover={{ scale: 1.05 }}
                      onError={(e) => {
                        console.log("Image failed to load:", firstImage);
                        e.target.src = "/placeholder.jpg";
                      }}
                    />

                    {colors.length > 0 && (
                      <div className="color-dots">
                        {colors.slice(0, 3).map((color, idx) => (
                          <span
                            key={idx}
                            style={{ backgroundColor: color.hex }}
                            title={color.name}
                          />
                        ))}
                        {colors.length > 3 && (
                          <span className="color-more">+{colors.length - 3}</span>
                        )}
                      </div>
                    )}

                    <h5>
                      {item.name}
                      {colorName && <span className="color-name"> {colorName}</span>}
                    </h5>

                    <p className="starting">STARTING FROM</p>
                    <h4 className="price">₹ {displayPrice.toLocaleString()}</h4>
                    <p className="gst">prices incl. of GST</p>

                    <NavLink
                      to={`/product-details/${item._id}`}
                      style={{ textDecoration: "none" }}
                    >
                      <motion.button
                        className="shop-btn text-dark"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Shop Now <FaArrowRight />
                      </motion.button>
                    </NavLink>
                  </motion.div>
                </Col>
              );
            })}
          </Row>
        )}
      </Container>

      <Footer />
    </div>
  );
};

export default Productgrid;